/** USD for tables. Sub-cent amounts keep two significant digits ($0.00042), so fractions of a cent never read as $0.00. */
export function formatUsd(value: number | undefined): string {
  const amount = Number.isFinite(value) ? (value as number) : 0;
  if (amount > 0 && amount < 0.01) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumSignificantDigits: 2 }).format(amount);
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: amount >= 10 ? 2 : 4,
  }).format(amount);
}

export function formatCount(value: number | undefined): string {
  return new Intl.NumberFormat('en-US').format(value ?? 0);
}

export function formatDate(iso: string | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

export function formatDateTime(iso: string | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

export function rangeForDays(days: number): { from: string; to: string } {
  const to = new Date();
  const from = new Date(
    Date.UTC(to.getUTCFullYear(), to.getUTCMonth(), to.getUTCDate() - (days - 1)),
  );
  const end = new Date(
    Date.UTC(to.getUTCFullYear(), to.getUTCMonth(), to.getUTCDate(), 23, 59, 59, 999),
  );
  return { from: from.toISOString(), to: end.toISOString() };
}

/** Full project lifecycle window: createdAt (or earliest usable day) through end of today UTC. */
export function rangeForLifecycle(createdAt: string | undefined): { from: string; to: string } {
  const to = new Date();
  const end = new Date(
    Date.UTC(to.getUTCFullYear(), to.getUTCMonth(), to.getUTCDate(), 23, 59, 59, 999),
  );
  const created = createdAt ? new Date(createdAt) : undefined;
  if (!created || Number.isNaN(created.getTime())) {
    return rangeForDays(365);
  }
  const from =
    created.getTime() > end.getTime()
      ? new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate()))
      : created;
  return { from: from.toISOString(), to: end.toISOString() };
}

export type OrgIdentityFields = {
  displayName?: string;
  name?: string;
  organizationId: string;
};

/** Prefer the organization's display name, then slug/name, then id. */
export function orgLabel(row: OrgIdentityFields): string {
  const display = row.displayName?.trim();
  const slug = row.name?.trim();
  if (display) return display;
  if (slug) return slug;
  return row.organizationId;
}

/** Secondary organization name when it differs from the display label. */
export function orgSecondaryLabel(row: OrgIdentityFields): string | undefined {
  const display = row.displayName?.trim();
  const slug = row.name?.trim();
  if (display && slug && slug.toLowerCase() !== display.toLowerCase()) return slug;
  return undefined;
}

export const ORG_PAGE_SIZE = 25;
export const PROJECT_PAGE_SIZE = 25;
export const ACTIVITY_PAGE_SIZE = 25;
export const CREDIT_PAGE_SIZE = 25;

/** Convert YYYY-MM-DD inputs into ISO from/to bounds (UTC day window). */
export function rangeFromCustomDates(fromDate: string, toDate: string): { from: string; to: string } | undefined {
  const fromMatch = /^(\d{4}-\d{2}-\d{2})$/.exec(fromDate.trim());
  const toMatch = /^(\d{4}-\d{2}-\d{2})$/.exec(toDate.trim());
  if (!fromMatch || !toMatch) return undefined;
  const from = new Date(`${fromMatch[1]}T00:00:00.000Z`);
  const to = new Date(`${toMatch[1]}T23:59:59.999Z`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return undefined;
  if (from.getTime() > to.getTime()) {
    return { from: to.toISOString(), to: from.toISOString() };
  }
  return { from: from.toISOString(), to: to.toISOString() };
}

export function isoToDateInput(iso: string | undefined): string {
  if (!iso) return '';
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(iso);
  return match?.[1] || '';
}
